import React from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MultiSelectTags } from './MultiSelectTags';
import { Textarea } from './ui/textarea';

const personalityTagsOptions = [
  'Happening', 'Funaholic', 'Caring', 'Chill', 'Adventurous', 'Ambitious', 'Artistic', 'Athletic',
  'Charming', 'Cheerful', 'Compassionate', 'Confident', 'Creative', 'Curious', 'Dependable',
  'Determined', 'Easygoing', 'Empathetic', 'Energetic', 'Enthusiastic', 'Extroverted',
  'Flexible', 'Friendly', 'Funny', 'Generous', 'Gentle', 'Genuine', 'Goal-oriented', 'Gracious',
  'Hardworking', 'Helpful', 'Honest', 'Hopeful', 'Humble', 'Humorous', 'Imaginative',
  'Independent', 'Innovative', 'Intelligent', 'Introverted', 'Intuitive', 'Kind', 'Leader',
  'Loyal', 'Mature', 'Motivated', 'Optimistic', 'Organized', 'Outgoing', 'Passionate',
  'Patient', 'Peaceful', 'Perfectionist', 'Persistent', 'Playful', 'Polite', 'Positive',
  'Practical', 'Professional', 'Quiet', 'Rational', 'Realistic', 'Reflective', 'Reliable',
  'Reserved', 'Resilient', 'Resourceful', 'Respectful', 'Responsible', 'Romantic', 'Self-aware',
  'Selfless', 'Sensitive', 'Serious', 'Shy', 'Sincere', 'Sociable', 'Sophisticated', 'Spontaneous',
  'Stable', 'Strong', 'Supportive', 'Sweet', 'Tactful', 'Thoughtful', 'Tolerant', 'Traditional',
  'Trusting', 'Trustworthy', 'Understanding', 'Unique', 'Versatile', 'Warm', 'Wise', 'Witty',
  'Worldly', 'Zestful', 'Adaptable', 'Affectionate', 'Alert', 'Analytical', 'Articulate',
  'Assertive', 'Attentive', 'Balanced', 'Bold', 'Brave', 'Bright', 'Calm', 'Capable'
];

const booksGenreOptions = [
  'Fantasy', 'Horror', 'Mystery', 'Romance', 'Science Fiction', 'Thriller', 'Biography',
  'Self-Help', 'History', 'Poetry', 'Drama', 'Adventure', 'Crime', 'Philosophy',
  'Travel', 'Comedy', 'Young Adult', 'Classic', 'Contemporary', 'Historical Fiction'
];

const musicGenreOptions = [
  'Pop', 'Rock', 'Hip Hop', 'Jazz', 'Classical', 'Electronic', 'Country', 'R&B',
  'Blues', 'Reggae', 'Folk', 'Metal', 'Indie', 'Soul', 'Funk', 'Latin',
  'Alternative', 'Dance', 'Punk', 'Disco', 'Gospel', 'Techno', 'House', 'Bollywood',
  'Sufi', 'Devotional', 'Ghazal', 'Qawwali', 'Carnatic', 'Hindustani'
];

interface ExtendedProfileFieldsProps {
  formData: any;
  onChange: (field: string, value: any) => void;
  errors?: any;
  touched?: any;
}

export function ExtendedProfileFields({ formData, onChange, errors, touched }: ExtendedProfileFieldsProps) {
  return (
    <div className="space-y-6">
      {/* Health & Medical Section */}
      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm space-y-4">
        <h3 className="text-base md:text-lg text-gray-900 flex items-center gap-2">
          <span className="text-red-500">❤️</span>
          Health & Medical Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="thalassemia">Thalassemia</Label>
            <Select
              value={formData.thalassemia}
              onValueChange={(value) => onChange('thalassemia', value)}
            >
              <SelectTrigger id="thalassemia">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="No">No</SelectItem>
                <SelectItem value="Yes">Yes</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="hivPositive">HIV Status</Label>
            <Select
              value={formData.hivPositive}
              onValueChange={(value) => onChange('hivPositive', value)}
            >
              <SelectTrigger id="hivPositive">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="No">Negative</SelectItem>
                <SelectItem value="Yes">Positive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Personality & Interests */}
      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm space-y-4">
        <h3 className="text-base md:text-lg text-gray-900 flex items-center gap-2">
          <span className="text-purple-500">✨</span>
          Personality & Interests
        </h3>

        <MultiSelectTags
          label="My Personality"
          options={personalityTagsOptions}
          selectedTags={formData.personalityTags || []}
          onChange={(tags) => onChange('personalityTags', tags)}
          placeholder="Search personality traits..."
          maxTags={10}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="horoscopeSign">Horoscope Sign</Label>
            <Select
              value={formData.horoscopeSign}
              onValueChange={(value) => onChange('horoscopeSign', value)}
            >
              <SelectTrigger id="horoscopeSign">
                <SelectValue placeholder="Select sign" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Aries">Aries</SelectItem>
                <SelectItem value="Taurus">Taurus</SelectItem>
                <SelectItem value="Gemini">Gemini</SelectItem>
                <SelectItem value="Cancer">Cancer</SelectItem>
                <SelectItem value="Leo">Leo</SelectItem>
                <SelectItem value="Virgo">Virgo</SelectItem>
                <SelectItem value="Libra">Libra</SelectItem>
                <SelectItem value="Scorpio">Scorpio</SelectItem>
                <SelectItem value="Sagittarius">Sagittarius</SelectItem>
                <SelectItem value="Capricorn">Capricorn</SelectItem>
                <SelectItem value="Aquarius">Aquarius</SelectItem>
                <SelectItem value="Pisces">Pisces</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="dressStyle">Dress Style</Label>
            <Select
              value={formData.dressStyle}
              onValueChange={(value) => onChange('dressStyle', value)}
            >
              <SelectTrigger id="dressStyle">
                <SelectValue placeholder="Select style" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Casual">Casual</SelectItem>
                <SelectItem value="Formal">Formal</SelectItem>
                <SelectItem value="Traditional">Traditional</SelectItem>
                <SelectItem value="Trendy">Trendy</SelectItem>
                <SelectItem value="Sporty">Sporty</SelectItem>
                <SelectItem value="Bohemian">Bohemian</SelectItem>
                <SelectItem value="Minimalist">Minimalist</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <MultiSelectTags
          label="Favorite Books Genre"
          options={booksGenreOptions}
          selectedTags={formData.favoriteBooksGenre || []}
          onChange={(tags) => onChange('favoriteBooksGenre', tags)}
          placeholder="Search book genres..."
          maxTags={5}
        />

        <MultiSelectTags
          label="Favorite Music Genre"
          options={musicGenreOptions}
          selectedTags={formData.favoriteMusicGenre || []}
          onChange={(tags) => onChange('favoriteMusicGenre', tags)}
          placeholder="Search music genres..."
          maxTags={5}
        />
      </div>

      {/* Lifestyle & Possessions */}
      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm space-y-4">
        <h3 className="text-base md:text-lg text-gray-900 flex items-center gap-2">
          <span className="text-green-500">🏠</span>
          Lifestyle & Possessions
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="ownHouse">Own a House</Label>
            <Select
              value={formData.ownHouse}
              onValueChange={(value) => onChange('ownHouse', value)}
            >
              <SelectTrigger id="ownHouse">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="ownCar">Own a Car</Label>
            <Select
              value={formData.ownCar}
              onValueChange={(value) => onChange('ownCar', value)}
            >
              <SelectTrigger id="ownCar">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="openToPets">Open to Pets</Label>
            <Select
              value={formData.openToPets}
              onValueChange={(value) => onChange('openToPets', value)}
            >
              <SelectTrigger id="openToPets">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Additional Family Information */}
      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm space-y-4">
        <h3 className="text-base md:text-lg text-gray-900 flex items-center gap-2">
          <span className="text-blue-500">👨‍👩‍👧‍👦</span>
          Additional Family Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="familyIncome">Family Income</Label>
            <Input
              id="familyIncome"
              type="text"
              value={formData.familyIncome}
              onChange={(e) => onChange('familyIncome', e.target.value)}
              placeholder="e.g., 10-15 LPA"
            />
          </div>

          <div>
            <Label htmlFor="livingWith">Living With</Label>
            <Select
              value={formData.livingWith}
              onValueChange={(value) => onChange('livingWith', value)}
            >
              <SelectTrigger id="livingWith">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Parents">Parents</SelectItem>
                <SelectItem value="Alone">Alone</SelectItem>
                <SelectItem value="Relatives">Relatives</SelectItem>
                <SelectItem value="Friends">Friends</SelectItem>
                <SelectItem value="Hostel">Hostel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="familyBasedOutOf">Family Based Out Of</Label>
            <Input
              id="familyBasedOutOf"
              type="text"
              value={formData.familyBasedOutOf}
              onChange={(e) => onChange('familyBasedOutOf', e.target.value)}
              placeholder="City/Location"
            />
          </div>
        </div>
      </div>

      {/* Hobbies & Entertainment */}
      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm space-y-4">
        <h3 className="text-base md:text-lg text-gray-900 flex items-center gap-2">
          <span className="text-orange-500">🎭</span>
          Hobbies & Entertainment
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="foodICook">Food I Cook</Label>
            <Input
              id="foodICook"
              type="text"
              value={formData.foodICook}
              onChange={(e) => onChange('foodICook', e.target.value)}
              placeholder="e.g., Italian, Indian..."
            />
          </div>

          <div>
            <Label htmlFor="cuisine">Favorite Cuisine</Label>
            <Input
              id="cuisine"
              type="text"
              value={formData.cuisine}
              onChange={(e) => onChange('cuisine', e.target.value)}
              placeholder="e.g., Mexican, Thai..."
            />
          </div>

          <div>
            <Label htmlFor="sports">Sports</Label>
            <Input
              id="sports"
              type="text"
              value={formData.sports}
              onChange={(e) => onChange('sports', e.target.value)}
              placeholder="e.g., Cricket, Football..."
            />
          </div>

          <div>
            <Label htmlFor="vacationDestination">Vacation Destination</Label>
            <Input
              id="vacationDestination"
              type="text"
              value={formData.vacationDestination}
              onChange={(e) => onChange('vacationDestination', e.target.value)}
              placeholder="e.g., Mountains, Beach..."
            />
          </div>

          <div>
            <Label htmlFor="movies">Favorite Movies</Label>
            <Input
              id="movies"
              type="text"
              value={formData.movies}
              onChange={(e) => onChange('movies', e.target.value)}
              placeholder="e.g., Action, Comedy..."
            />
          </div>

          <div>
            <Label htmlFor="tvShows">Favorite TV Shows</Label>
            <Input
              id="tvShows"
              type="text"
              value={formData.tvShows}
              onChange={(e) => onChange('tvShows', e.target.value)}
              placeholder="e.g., Friends, Game of Thrones..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
